import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Per-route SEO updater — no external deps. Mutates the DOM head in a useEffect
 * whenever the route or provided props change. Base OG/Twitter tags live in
 * index.html; this component overrides title/description/canonical/og:url
 * per page for search engines that respect client-rendered updates
 * (Googlebot does; static crawlers rely on the index.html defaults).
 */
const BASE = "https://islandgold.eigooinc.net";

function setMeta(selector, attr, value) {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector
            .replace(/^meta\[/, "")
            .replace(/\]$/, "")
            .split("=");
        el.setAttribute(k, v.replace(/["']/g, ""));
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
}

function setLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export default function Seo({ title, description, image }) {
    const { pathname } = useLocation();

    useEffect(() => {
        const fullTitle = title
            ? `${title} · Island Gold Glow Soap Co.`
            : "Island Gold Glow Soap Co.";
        document.title = fullTitle;

        const desc =
            description ||
            "Island Gold Glow Soap Co. — handcrafted volcanic mineral soap, handmade in Hawaiʻi. Small batches, island botanicals.";
        setMeta('meta[name="description"]', "content", desc);
        setMeta('meta[name="robots"]', "content", "index, follow");

        const url = `${BASE}${pathname}`;
        setLink("canonical", url);

        // Open Graph
        setMeta('meta[property="og:title"]', "content", fullTitle);
        setMeta('meta[property="og:description"]', "content", desc);
        setMeta('meta[property="og:type"]', "content", "website");
        setMeta('meta[property="og:url"]', "content", url);
        setMeta(
            'meta[property="og:image"]',
            "content",
            image || `${BASE}/og-image.png`,
        );

        // Twitter
        setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
        setMeta('meta[name="twitter:title"]', "content", fullTitle);
        setMeta('meta[name="twitter:description"]', "content", desc);
        setMeta(
            'meta[name="twitter:image"]',
            "content",
            image || `${BASE}/og-image.png`,
        );
    }, [pathname, title, description, image]);

    return null;
}
